import { isBoxCategory } from "@loot-os/api-contract";
import type { CatalogItemStatus, Circulation } from "@loot-os/core/db/schema";
import { taxonomyFromBoxedTags } from "@loot-os/fulfillment-core/catalog/from-boxed";
import { gameForItem } from "@loot-os/fulfillment-core/catalog/game";
import type { AvailabilityStrategy, PricingSource } from "@loot-os/fulfillment-core/catalog/item";
import {
  printingsForCardGame,
  TCG_CARD_GAMES,
  type TcgCardGame,
} from "@loot-os/fulfillment-core/catalog/tcg-import";
import { printingForItem } from "@loot-os/fulfillment-core/procurement/tcgplayer-url";
import type { AppRouter } from "@loot-os/functions/src/dashboard-api/trpc/router.js";
import type { inferInput, inferOutput, TRPCOptionsProxy } from "@trpc/tanstack-react-query";

import {
  CATALOG_ITEM_KINDS,
  CATALOG_ITEM_STATUSES,
  type CatalogItemKind,
  type CatalogItemStatusChoice,
  CIRCULATION_OVERRIDES,
  type CirculationOverride,
  isMember,
} from "./catalogItemCreate";

const HIGH_ERROR = "HIGH_ERROR";
const COMPARE_AT_LABEL_MAX = 40;

/** One catalog item as the edit page reads it. */
export type CatalogItemEditRecord = inferOutput<
  TRPCOptionsProxy<AppRouter>["platform"]["fulfillment"]["items"]["get"]
>;

type CatalogItemUpdate = inferInput<
  TRPCOptionsProxy<AppRouter>["platform"]["fulfillment"]["items"]["update"]
>;

/**
 * The edit form. Money is dollars until submit. Kind `UNSET` keeps a boxed single
 * (no type) a single, and a custom item with no type under Unclassified.
 */
export interface CatalogItemEditDraft {
  kind: CatalogItemKind | "UNSET";
  name: string;
  collectionName: string;
  sourceId: string;
  cardGame: string;
  printing: string;
  image: string;
  valueDollars: string;
  marketPriceDollars: string;
  compareAtValueDollars: string;
  compareAtLabelOverride: string;
  pricingSource: PricingSource;
  availabilityStrategy: AvailabilityStrategy;
  status: CatalogItemStatusChoice;
  overrideCirculation: CirculationOverride | "";
  quantity: string;
  tenantId: string;
  highError: boolean;
  rocaId: string;
}

export type CatalogItemEditErrors = Partial<Record<keyof CatalogItemEditDraft, string>>;

export type CatalogItemEditParse =
  | { ok: true; update: CatalogItemUpdate | null }
  | { ok: false; errors: CatalogItemEditErrors };

/** Fill the form from the stored item. A stored value is left as typed, not recomputed. */
export function draftFromCatalogItem(item: CatalogItemEditRecord): CatalogItemEditDraft {
  return {
    kind: kindOf(item.type),
    name: item.name,
    collectionName: stringAttr(item.attributes, "collectionName"),
    sourceId: item.sourceId ?? "",
    cardGame: gameForItem(item) ?? "",
    printing: printingForItem({ pricingRef: item.pricingRef, attributes: item.attributes }) ?? "",
    image: item.overrideImage ?? item.image ?? "",
    valueDollars: centsToDollars(item.value),
    marketPriceDollars: centsToDollars(item.marketPrice),
    compareAtValueDollars: centsToDollars(item.compareAtValue),
    compareAtLabelOverride: item.compareAtLabelOverride ?? "",
    pricingSource: item.pricingSource,
    availabilityStrategy: item.availabilityStrategy,
    status: statusOf(item.status),
    overrideCirculation: circulationOf(item.overrideCirculation),
    quantity: String(item.quantity),
    tenantId: item.tenantId ?? "",
    highError: item.tags.includes(HIGH_ERROR),
    rocaId: item.rocaId ?? "",
  };
}

/**
 * The update to send, or the field errors that block it. Null when nothing changed.
 * Tags, pricing ref and attributes are merged: a game change replaces the game tag,
 * and a printing or product id is written into the objects that already hold it.
 */
export function toCatalogItemUpdate(
  item: CatalogItemEditRecord,
  draft: CatalogItemEditDraft,
): CatalogItemEditParse {
  const errors: CatalogItemEditErrors = {};
  const name = draft.name.trim();
  if (!name) errors.name = "Full name is required.";

  const manual = isManualKind(draft.kind);
  const sourced = sourceRequired(item, draft.kind);
  const game = draft.cardGame.trim();
  if (game && !isBoxCategory(game)) errors.cardGame = "Choose a card game.";

  const printing = draft.printing.trim();
  const printingGame = tcgCardGame(game);
  if (
    printingGame &&
    printing &&
    printing !== storedPrinting(item) &&
    !printingsForCardGame(printingGame).includes(printing)
  ) {
    errors.printing = "Choose a printing for this game.";
  }
  if (sourced && !printing && errors.printing === undefined) {
    errors.printing = "Card printing is required.";
  }

  const sourceId = draft.sourceId.trim();
  if (sourced && !sourceId) errors.sourceId = "Source id is required.";

  const collectionName = draft.collectionName.trim();
  if (manual && !collectionName) errors.collectionName = "Collection name is required.";

  const marketPrice = manual
    ? requireDollars(draft.marketPriceDollars, "marketPriceDollars", errors, "Market price")
    : readDollars(draft.marketPriceDollars, "marketPriceDollars", errors);
  const value = manual
    ? requireDollars(draft.valueDollars, "valueDollars", errors, "Value")
    : readDollars(draft.valueDollars, "valueDollars", errors);
  const quantity = wholeNumber(draft.quantity);
  if (quantity === null || quantity === "invalid") {
    errors.quantity = "Enter a whole number of 0 or more.";
  }

  const compared = readCompareAt(draft, value, errors);
  if (Object.keys(errors).length > 0 || !compared || quantity === null || quantity === "invalid") {
    return { ok: false, errors };
  }

  const patch: CatalogItemUpdate["patch"] = {};
  assign(patch, "name", name, item.name);
  assign(patch, "pricingSource", draft.pricingSource, item.pricingSource);
  assign(patch, "availabilityStrategy", draft.availabilityStrategy, item.availabilityStrategy);
  assign(patch, "status", itemStatus(draft.status), item.status);
  assign(
    patch,
    "overrideCirculation",
    draft.overrideCirculation ? circulationValue(draft.overrideCirculation) : null,
    item.overrideCirculation,
  );
  assign(patch, "quantity", quantity, item.quantity);
  assign(patch, "value", value, item.value);
  assign(patch, "marketPrice", marketPrice, item.marketPrice);
  assign(patch, "compareAtValue", compared.compareAtValue, item.compareAtValue);
  assign(patch, "compareAtLabelOverride", compared.label || null, item.compareAtLabelOverride);
  assign(patch, "tenantId", blankToNull(draft.tenantId), item.tenantId);
  assign(patch, "type", nextType(item.type, draft.kind), item.type);
  assign(patch, "sourceId", sourceId || null, item.sourceId);

  const tags = nextTags(item, game, draft.highError);
  assign(patch, "tags", tags, item.tags);
  if (game !== (gameForItem(item) ?? "")) {
    if (game && isBoxCategory(game)) {
      const taxonomy = taxonomyFromBoxedTags([game]);
      assign(patch, "category", taxonomy.category, item.category);
      assign(patch, "subcategory", taxonomy.subcategory, item.subcategory);
    } else {
      assign(patch, "category", null, item.category);
      assign(patch, "subcategory", null, item.subcategory);
    }
  }

  assign(patch, "attributes", nextAttributes(item, draft), item.attributes);
  assign(patch, "pricingRef", nextPricingRef(item, draft), item.pricingRef);
  assignImage(patch, item, draft.image);

  const nextRoca = draft.rocaId.trim() || null;
  const rocaChanged = nextRoca !== item.rocaId;
  if (Object.keys(patch).length === 0 && !rocaChanged) return { ok: true, update: null };
  return {
    ok: true,
    update: { id: item.id, patch, ...(rocaChanged ? { rocaId: nextRoca } : {}) },
  };
}

function kindOf(type: string | null): CatalogItemKind | "UNSET" {
  if (type === "SINGLE" || type === "SINGLES") return "SINGLE";
  if (isMember(CATALOG_ITEM_KINDS, type) && type !== "SINGLE") return type;
  return "UNSET";
}

function isManualKind(kind: CatalogItemEditDraft["kind"]): boolean {
  return kind === "SLABBED" || kind === "SEALED" || kind === "OTHER";
}

/** A single, and a boxed TCG row whose kind was never set, is priced from a product id. */
function sourceRequired(item: CatalogItemEditRecord, kind: CatalogItemEditDraft["kind"]): boolean {
  if (isManualKind(kind)) return false;
  if (kind === "SINGLE") return true;
  return stringAttr(item.attributes, "boxedProtocol") === "TCG";
}

function nextType(type: string | null, kind: CatalogItemEditDraft["kind"]): string | null {
  if (kindOf(type) === kind) return type;
  return kind === "UNSET" ? null : kind;
}

function nextTags(item: CatalogItemEditRecord, game: string, highError: boolean): string[] {
  const shown = gameForItem(item) ?? "";
  let tags = item.tags.filter((tag) => tag !== HIGH_ERROR);
  if (game !== shown) {
    tags = tags.filter((tag) => !isBoxCategory(tag));
    if (game && isBoxCategory(game)) tags.push(game);
  }
  if (highError) tags.push(HIGH_ERROR);
  return tags;
}

function nextAttributes(
  item: CatalogItemEditRecord,
  draft: CatalogItemEditDraft,
): Record<string, unknown> | null {
  const attrs = { ...(item.attributes ?? {}) };
  if (kindOf(item.type) !== draft.kind) {
    if (draft.kind === "SINGLE") attrs.boxedProtocol = "TCG";
    else if (isManualKind(draft.kind)) attrs.boxedProtocol = "CUSTOM_TCG";
  }

  const collection = draft.collectionName.trim();
  if (collection) attrs.collectionName = collection;
  else delete attrs.collectionName;

  const printing = draft.printing.trim();
  const printingMoved =
    typeof attrs.printing === "string" ||
    (isManualKind(draft.kind) &&
      (printing !== storedPrinting(item) || kindOf(item.type) !== draft.kind));
  if (printingMoved) {
    if (printing) attrs.printing = printing;
    else delete attrs.printing;
  }
  const filter = attrs.filter;
  if (isRecord(filter)) {
    attrs.filter = withStringKey(
      withStringKey(filter, "printing", printing),
      "productId",
      draft.sourceId.trim(),
    );
  }
  const details = attrs.details;
  if (isRecord(details)) {
    let next = details;
    if ("printing" in next) next = withStringKey(next, "printing", printing);
    if ("category" in next && draft.cardGame.trim() !== shownGame(item)) {
      next = withStringKey(next, "category", draft.cardGame.trim());
    }
    attrs.details = next;
  }
  return Object.keys(attrs).length === 0 ? null : attrs;
}

function nextPricingRef(
  item: CatalogItemEditRecord,
  draft: CatalogItemEditDraft,
): Record<string, unknown> | null {
  if (!item.pricingRef && isManualKind(draft.kind)) return null;
  const next = { ...(item.pricingRef ?? {}) };
  const printing = draft.printing.trim();
  const sourceId = draft.sourceId.trim();
  if (printing) next.printing = printing;
  else delete next.printing;
  if (sourceId) next.productId = sourceId;
  else delete next.productId;
  if (!item.pricingRef && Object.keys(next).length === 0) return null;
  return Object.keys(next).length === 0 ? null : next;
}

function assignImage(
  patch: CatalogItemUpdate["patch"],
  item: CatalogItemEditRecord,
  image: string,
): void {
  const shown = item.overrideImage ?? item.image ?? "";
  const next = image.trim();
  if (next === shown) return;
  if (next) patch.overrideImage = next;
  else if (item.overrideImage) patch.overrideImage = null;
  else patch.image = null;
}

function shownGame(item: CatalogItemEditRecord): string {
  return gameForItem(item) ?? "";
}

function storedPrinting(item: CatalogItemEditRecord): string {
  return printingForItem({ pricingRef: item.pricingRef, attributes: item.attributes }) ?? "";
}

function withStringKey(
  record: Record<string, unknown>,
  key: string,
  value: string,
): Record<string, unknown> {
  if (!value) {
    const next = { ...record };
    delete next[key];
    return next;
  }
  return { ...record, [key]: value };
}

function stringAttr(attributes: Record<string, unknown> | null, key: string): string {
  const value = attributes?.[key];
  return typeof value === "string" ? value : "";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function statusOf(status: CatalogItemStatus): CatalogItemStatusChoice {
  return isMember(CATALOG_ITEM_STATUSES, status) ? status : "STANDARD";
}

function circulationOf(value: Circulation | null): CirculationOverride | "" {
  return value && isMember(CIRCULATION_OVERRIDES, value) ? value : "";
}

/** The form's names are the schema enum's values; catalog-item.test.ts keeps the lists equal. */
function itemStatus(status: CatalogItemStatusChoice): CatalogItemStatus {
  return status as CatalogItemStatus;
}

function circulationValue(value: CirculationOverride): Circulation {
  return value as Circulation;
}

function tcgCardGame(value: string): TcgCardGame | null {
  return isMember(TCG_CARD_GAMES, value) ? value : null;
}

function assign<K extends keyof CatalogItemUpdate["patch"]>(
  patch: CatalogItemUpdate["patch"],
  key: K,
  next: CatalogItemUpdate["patch"][K],
  prev: CatalogItemUpdate["patch"][K],
): void {
  if (!sameValue(next, prev)) patch[key] = next;
}

function sameValue(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return false;
  if (Array.isArray(a) || Array.isArray(b)) {
    return (
      Array.isArray(a) &&
      Array.isArray(b) &&
      a.length === b.length &&
      a.every((value, index) => sameValue(value, b[index]))
    );
  }
  const aRecord = a as Record<string, unknown>;
  const bRecord = b as Record<string, unknown>;
  const keys = new Set([...Object.keys(aRecord), ...Object.keys(bRecord)]);
  for (const key of keys) if (!sameValue(aRecord[key], bRecord[key])) return false;
  return true;
}

function centsToDollars(cents: number | null): string {
  if (cents === null) return "";
  const whole = Math.floor(Math.abs(cents) / 100);
  const fraction = String(Math.abs(cents) % 100).padStart(2, "0");
  return `${cents < 0 ? "-" : ""}${whole}.${fraction}`;
}

function blankToNull(raw: string): string | null {
  const trimmed = raw.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function dollarsToCents(raw: string): number | null | "invalid" {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(trimmed);
  const whole = match?.[1];
  if (!match || whole === undefined) return "invalid";
  const fraction = (match[2] ?? "").padEnd(2, "0");
  return Number(whole) * 100 + Number(fraction);
}

function wholeNumber(raw: string): number | null | "invalid" {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (!/^\d+$/.test(trimmed)) return "invalid";
  return Number(trimmed);
}

function readDollars(
  raw: string,
  key: keyof CatalogItemEditDraft,
  errors: CatalogItemEditErrors,
): number | null {
  const parsed = dollarsToCents(raw);
  if (parsed === "invalid") {
    errors[key] = "Enter a dollar amount, like 12.50.";
    return null;
  }
  return parsed;
}

function requireDollars(
  raw: string,
  key: keyof CatalogItemEditDraft,
  errors: CatalogItemEditErrors,
  label: string,
): number | null {
  const parsed = readDollars(raw, key, errors);
  if (parsed === null && errors[key] === undefined) errors[key] = `${label} is required.`;
  return parsed;
}

function readCompareAt(
  draft: CatalogItemEditDraft,
  value: number | null,
  errors: CatalogItemEditErrors,
): { compareAtValue: number | null; label: string } | null {
  const compareAtValue = readDollars(draft.compareAtValueDollars, "compareAtValueDollars", errors);
  const label = draft.compareAtLabelOverride.trim();
  if (label && compareAtValue === null && errors.compareAtValueDollars === undefined) {
    errors.compareAtLabelOverride = "A compare-at label needs a compare-at value.";
  } else if (label.length > COMPARE_AT_LABEL_MAX) {
    errors.compareAtLabelOverride = "Compare-at label must be 40 characters or fewer.";
  }
  if (
    compareAtValue !== null &&
    errors.compareAtValueDollars === undefined &&
    errors.valueDollars === undefined &&
    compareAtValue <= (value ?? 0)
  ) {
    errors.compareAtValueDollars = "Compare-at value must be greater than value.";
  }
  return Object.keys(errors).length > 0 ? null : { compareAtValue, label };
}
